import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  CheckCircle2,
  XCircle,
  AlertCircle,
  Building2,
  Receipt,
  Tag,
  TrendingUp,
  Plus,
} from "lucide-react";
import { toast } from "sonner";

interface AnalysisResult {
  documentType: string;
  confidence: number;
  vendorCandidates?: Array<{
    name: string;
    uid?: string;
    category?: string;
    confidence: number;
  }>;
  transactionCandidates?: Array<{
    amount: number;
    date?: string;
    description?: string;
    category?: string;
    confidence: number;
  }>;
  entities?: {
    companies?: Array<{ name: string; uid?: string }>;
    amounts?: number[];
    dates?: string[];
    taxNumbers?: string[];
  };
  keywords?: string[];
  suggestedActions?: string[];
}

interface AnalysisReviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentName: string;
  analysis: AnalysisResult | null;
  onCreateVendor?: (vendor: any) => void;
  onCreateTransaction?: (transaction: any) => void;
}

export function AnalysisReviewModal({
  open,
  onOpenChange,
  documentName,
  analysis,
  onCreateVendor,
  onCreateTransaction,
}: AnalysisReviewModalProps) {
  if (!analysis) return null;

  const getConfidenceBadge = (confidence: number) => {
    if (confidence >= 70) {
      return (
        <Badge
          variant="outline"
          className="bg-[var(--success)]/10 text-[var(--success)] border-[var(--success)]/20"
        >
          <CheckCircle2 className="h-3 w-3 mr-1" />
          High ({confidence}%)
        </Badge>
      );
    } else if (confidence >= 40) {
      return (
        <Badge
          variant="outline"
          className="bg-[var(--warning)]/10 text-[var(--warning)] border-[var(--warning)]/20"
        >
          <AlertCircle className="h-3 w-3 mr-1" />
          Medium ({confidence}%)
        </Badge>
      );
    } else {
      return (
        <Badge
          variant="outline"
          className="bg-destructive/10 text-destructive border-destructive/20"
        >
          <XCircle className="h-3 w-3 mr-1" />
          Low ({confidence}%)
        </Badge>
      );
    }
  };

  const handleCreateVendor = (vendor: any) => {
    if (onCreateVendor) {
      onCreateVendor(vendor);
      toast.success(`Vendor "${vendor.name}" created successfully`);
    }
  };

  const handleCreateTransaction = (transaction: any) => {
    if (onCreateTransaction) {
      onCreateTransaction(transaction);
      toast.success("Transaction created successfully");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Analysis Results: {documentName}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Document Type */}
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">
                  Document Type
                </p>
                <p className="text-lg font-semibold capitalize">
                  {analysis.documentType.replace("_", " ")}
                </p>
              </div>
              {getConfidenceBadge(analysis.confidence)}
            </div>
          </Card>

          {/* Vendor Candidates */}
          {analysis.vendorCandidates &&
            analysis.vendorCandidates.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="h-5 w-5" />
                  <h3 className="text-lg font-semibold">Vendor Candidates</h3>
                </div>
                <div className="space-y-3">
                  {analysis.vendorCandidates.map((vendor, index) => (
                    <Card key={index} className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <p className="font-semibold">{vendor.name}</p>
                            {getConfidenceBadge(vendor.confidence)}
                          </div>
                          {vendor.uid && (
                            <p className="text-sm text-muted-foreground">
                              UID: {vendor.uid}
                            </p>
                          )}
                          {vendor.category && (
                            <Badge variant="secondary" className="mt-2">
                              {vendor.category}
                            </Badge>
                          )}
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleCreateVendor(vendor)}
                          disabled={!onCreateVendor}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Create Vendor
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

          {/* Transaction Candidates */}
          {analysis.transactionCandidates &&
            analysis.transactionCandidates.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Receipt className="h-5 w-5" />
                  <h3 className="text-lg font-semibold">
                    Transaction Candidates
                  </h3>
                </div>
                <div className="space-y-3">
                  {analysis.transactionCandidates.map((transaction, index) => (
                    <Card key={index} className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <p className="font-semibold text-lg">
                              CHF {transaction.amount.toFixed(2)}
                            </p>
                            {getConfidenceBadge(transaction.confidence)}
                          </div>
                          {transaction.date && (
                            <p className="text-sm text-muted-foreground">
                              Date:{" "}
                              {new Date(transaction.date).toLocaleDateString()}
                            </p>
                          )}
                          {transaction.description && (
                            <p className="text-sm mt-1">
                              {transaction.description}
                            </p>
                          )}
                          {transaction.category && (
                            <Badge variant="secondary" className="mt-2">
                              {transaction.category}
                            </Badge>
                          )}
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleCreateTransaction(transaction)}
                          disabled={!onCreateTransaction}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Create Transaction
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

          {/* Entities */}
          {analysis.entities && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Tag className="h-5 w-5" />
                <h3 className="text-lg font-semibold">Extracted Entities</h3>
              </div>
              <Card className="p-4">
                <div className="space-y-3">
                  {analysis.entities.companies &&
                    analysis.entities.companies.length > 0 && (
                      <div>
                        <p className="text-sm font-medium mb-2">Companies:</p>
                        <div className="flex flex-wrap gap-2">
                          {analysis.entities.companies.map((company, i) => (
                            <Badge key={i} variant="outline">
                              {company.name}
                              {company.uid && ` (${company.uid})`}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  {analysis.entities.amounts &&
                    analysis.entities.amounts.length > 0 && (
                      <div>
                        <p className="text-sm font-medium mb-2">Amounts:</p>
                        <div className="flex flex-wrap gap-2">
                          {analysis.entities.amounts.map((amount, i) => (
                            <Badge key={i} variant="outline">
                              CHF {amount.toFixed(2)}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  {analysis.entities.dates &&
                    analysis.entities.dates.length > 0 && (
                      <div>
                        <p className="text-sm font-medium mb-2">Dates:</p>
                        <div className="flex flex-wrap gap-2">
                          {analysis.entities.dates.map((date, i) => (
                            <Badge key={i} variant="outline">
                              {new Date(date).toLocaleDateString()}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  {analysis.entities.taxNumbers &&
                    analysis.entities.taxNumbers.length > 0 && (
                      <div>
                        <p className="text-sm font-medium mb-2">Tax Numbers:</p>
                        <div className="flex flex-wrap gap-2">
                          {analysis.entities.taxNumbers.map((taxNumber, i) => (
                            <Badge key={i} variant="outline">
                              {taxNumber}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                </div>
              </Card>
            </div>
          )}

          {/* Keywords */}
          {analysis.keywords && analysis.keywords.length > 0 && (
            <div>
              <p className="text-sm font-medium mb-2">Keywords:</p>
              <div className="flex flex-wrap gap-2">
                {analysis.keywords.map((keyword, i) => (
                  <Badge key={i} variant="secondary">
                    {keyword}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Suggested Actions */}
          {analysis.suggestedActions &&
            analysis.suggestedActions.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2">Suggested Actions:</p>
                <Card className="p-4">
                  <ul className="space-y-2">
                    {analysis.suggestedActions.map((action, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <AlertCircle className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                        <span>{action}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              </div>
            )}
        </div>

        <Separator />

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
